import getStoreProperty from "components/ProjectNonSpecificComponents/DevTools/Functions/getStoreProperty"
import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import useScreenType from "components/ProjectNonSpecificComponents/Hooks/useScreenType"
import AppRequests from "components/ProjectSpecificComponents/API/AppRequests/AppRequests"
import { HEIGHT_WITH_NAV_AND_FOOTER } from "components/ProjectSpecificComponents/Constants/GLOBAL_CONSTANTS"
import ROUTES from "components/ProjectSpecificComponents/Constants/ROUTES"
import { useFormStore } from "components/ProjectSpecificComponents/Contexts/FormStoreContext"
import { useStore } from "components/ProjectSpecificComponents/Contexts/GlobalStoreContext"
import {
	IFormsConfig,
	ISettings,
} from "components/ProjectSpecificComponents/Interfaces/FormsInterface"
import {
	TOnClickTableRow,
	TTableHeadCell,
} from "components/ProjectSpecificComponents/Interfaces/TableInterface"
import { observer } from "mobx-react"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router"
import { lostOrFinishedAdminSession } from "../API/Functions/lostOrFinishedAdminSession"
import IconButton from "../Elements/Global/atoms/Buttons/IconButton"
import PreferencesSmallButton from "../Elements/Global/atoms/Buttons/PreferencesSmallButton"
import NewConfigurationModal from "../Elements/Global/molecules/Modals/NewConfigurationModal"
import ConfigSectionButtons from "../Elements/Navigation/atoms/ConfigSectionButtons"
import { Td, Tr } from "../Elements/Tables/organisms/Table/StyledComponents/TableStyleComp"
import Table from "../Elements/Tables/organisms/Table/Table"
import { getDefaultFormData } from "../Stores/FormStore/defaults"

interface Props {}

const FormConfigurationOverviewTable = observer(({}: Props) => {
	const formStore = useFormStore()
	const store = useStore()
	const [isNewConfigModalShown, setIsNewConfigModalShown] = useState(false)
	const [modalMode, setModalMode] = useState<"new" | "edit">("new")
	const [records, setRecords] = useState<IFormsConfig[]>([])
	const { t } = useTranslation()
	const history = useHistory()
	const { isMobile } = useScreenType()

	const head: TTableHeadCell[] = [
		{
			key: "position",
			label: t("forms.field-label.number"),
			searchable: true,
			sortable: true,
		},
		{
			key: "isActive",
			label: t("forms.field-label.status"),
			searchable: true,
			sortable: true,
		},
		{
			key: "name",
			label: t("forms.field-label.name"),
			searchable: true,
			sortable: true,
		},
		{
			key: "nameURL",
			label: t("forms.field-label.name-url"),
			searchable: true,
			sortable: true,
		},
		{
			key: "actions",
			label: t("forms.field-label.actions"),
			searchable: false,
			sortable: false,
			disabled: true,
		},
	]

	const defaultNewConfigValues = {
		name: "",
		nameURL: "",
		position: formStore.settingsData.formsConfigs.length ?? 0,
		isActive: false,
	}

	useEffect(() => {
		fetchSavedSettingsAndCheckConnection()
	}, [])

	const fetchSavedSettingsAndCheckConnection = async () => {
		store.setLoaderOverlayOpen(true)
		const { ok, data, statusCode } = await AppRequests("Setting").get<ISettings>(
			"get-settings-admin"
		)
		store.triggerSessionTimeout()
		store.setLoaderOverlayOpen(false)

		if (ok && data) {
			formStore.mergeSettingsDataSafely(data)
		} else if (statusCode == 401) {
			lostOrFinishedAdminSession(history, store)
		} else {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "settings.failedTotal",
				descriptionKey: "settings.totalFail",
			})
		}

		setRecords(formStore.settingsData.formsConfigs)
	}

	const deleteFormsConfiguration = async (id: string) => {
		if (!id) return

		if (formStore.isFormsConfigurationActiveId(id)) {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "config.delete-failed",
				descriptionKey: "config.delete-failed-description",
			})
			return
		}

		deleteConfig(id, "config.deleted")
	}

	const deleteConfig = async (id: string, alertTitle?: string) => {
		store.setLoaderOverlayOpen(true)
		//Have to clean state to uptade table
		setRecords([])
		const { ok, data, statusCode } = await AppRequests("Setting").delete<ISettings>(
			"deleteFormsConfig",
			{
				formsConfigId: id,
			}
		)
		if (ok && data) {
			formStore.mergeSettingsDataSafely(data)
			store.setInfoModalConfig({
				type: "info",
				titleKey: alertTitle ?? "data-saving.success",
				showDuration: 1000,
			})
		} else if (statusCode == 401) {
			lostOrFinishedAdminSession(history, store)
		} else {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "data-saving.failed",
			})
		}
		setRecords(formStore.settingsData.formsConfigs)
		store.setLoaderOverlayOpen(false)
	}

	const postSettingsNewConfig = async (formData: Partial<IFormsConfig>) => {
		formStore.insertNewFormsConfiguration(formData)
		let newFormData = formStore.selectedFormsConfigId

		if (!newFormData) return
		if (!store.hasConnectionBeenMade && formData.isActive) {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "",
				descriptionKey: "rawText.connect-before-activate",
			})
			return
		}
		store.setLoaderOverlayOpen(true)
		//Have to clean state to uptade table
		setRecords([])

		const useCopiedConfigAsDefaultData: boolean = !!formStore.copiedConfig

		const { name, position, isActive, id, nameURL } = newFormData
		const baseConfig = useCopiedConfigAsDefaultData
			? {
					...formStore.copiedConfig,
					name,
					nameURL,
					position,
					isActive,
					id,
			  }
			: newFormData

		const { ok, data, statusCode } = await AppRequests("Setting").post<
			Partial<IFormsConfig>,
			ISettings
		>("saveNewFormsConfig", baseConfig)
		if (ok && data) {
			formStore.mergeSettingsDataSafely(data)
		} else if (statusCode == 401) {
			lostOrFinishedAdminSession(history, store)
		} else {
			formStore.deleteFormsConfiguration(newFormData.position)
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "settings.failed",
				descriptionKey: "settings.failedDescription",
			})
		}
		setRecords(formStore.settingsData.formsConfigs)
		store.setLoaderOverlayOpen(false)
	}

	const updateConfig = async (formData?: Partial<IFormsConfig>, alertTitle?: string) => {
		if (!formData) return
		// not posibble activate without connection made
		if (!store.hasConnectionBeenMade && formData.isActive) {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "",
				descriptionKey: "rawText.connect-before-activate",
			})
			return
		}

		store.setLoaderOverlayOpen(true)
		//Have to clean state to uptade table
		setRecords([])
		const { ok, data, statusCode } = await AppRequests("Setting").put<
			Partial<IFormsConfig>,
			ISettings
		>("updateFormsConfig", formData, {
			formsConfigId: formData.id,
		})
		if (ok && data) {
			formStore.mergeSettingsDataSafely(data)
			store.setInfoModalConfig({
				type: "info",
				titleKey: alertTitle ?? "data-saving.success",
				showDuration: 1000,
			})
		} else if (statusCode == 401) {
			lostOrFinishedAdminSession(history, store)
		} else {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "data-saving.failed",
			})
		}
		setRecords(formStore.settingsData.formsConfigs)
		store.setLoaderOverlayOpen(false)
	}

	useEffect(() => {
		formStore.setMainFieldsData([])
		const isConnected: string | null = sessionStorage.getItem("checkConnection")
		if (isConnected === "true") store.setHasConnectionBeenMade(true)
	}, [])

	const onClickTableRow: TOnClickTableRow = (rowId, row) => {
		if (!rowId) return

		if (!store.hasConnectionBeenMade) {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "",
				descriptionKey: "rawText.connect-before-edit",
			})
			return
		}
		formStore.setSelectedFormName(row?.name)
		formStore.setSelectedForm(String(rowId))
		formStore.setSelectedFormsConfig(row?.position)
		formStore.setSelectedConfigFormBeforeEdit()
		history.push(ROUTES.FORM_CONFIGURATION)
	}

	const tableRow = (row: IFormsConfig) => {
		const position = row["position"]
		const isActive = row["isActive"]
		const name = row["name"]
		const id = row["id"]
		const nameURL = row["nameURL"]
		return (
			<>
				<Td>{position + 1}</Td>
				<Td>
					<HyperDiv>
						<HyperDiv
							className="w-fit-content"
							padding="0.3rem"
							style={{
								backgroundColor: isActive ? "var(--active)" : "var(--gray-ec)",
								border: isActive ? " 1px solid var(--active)" : " 1px solid var(--gray-ec)",
								borderRadius: "5px",
							}}
						>
							{isActive ? t("rawText.active") : t("rawText.inactive")}
						</HyperDiv>
					</HyperDiv>
				</Td>
				<Td>{name}</Td>
				<Td>{nameURL}</Td>
				<Td>
					<HyperDiv direction="row" centered alignedRight>
						<IconButton
							style={{ width: "31px", height: "31px" }}
							iconWidth="15px"
							iconHeight="15px"
							iconColor="var(--red-b2)"
							iconKey="cross"
							backgroundColorOnHover="var(--red-b2)"
							iconColorOnHover="var(--white)"
							onClick={(e) => {
								deleteFormsConfiguration(id)
								e.stopPropagation()
							}}
						/>

						<Whitespace h x="0.1" />
						<IconButton
							style={{ width: "31px", height: "31px" }}
							iconWidth="15px"
							iconHeight="15px"
							iconKey="pencil"
							backgroundColorOnHover="var(--blue)"
							iconColorOnHover="var(--white)"
							onClick={(e) => {
								setIsNewConfigModalShown(true)
								setModalMode("edit")
								formStore.setSelectedForm(id)
								e.stopPropagation()
							}}
						/>
						<Whitespace h x="2" />
					</HyperDiv>
				</Td>
			</>
		)
	}

	return (
		<HyperDiv height={HEIGHT_WITH_NAV_AND_FOOTER} style={{ overflowY: "auto" }}>
			<ConfigSectionButtons />
			<HyperDiv padding={isMobile ? "" : "0 5rem"}>
				<Table
					search
					tableName={t("rawText.req-configuration-list")}
					head={head}
					records={records}
					tableRow={tableRow}
					clickedOnRow={onClickTableRow}
					fixedColumnWidth
					HeaderExtraElement={
						<PreferencesSmallButton
							onClick={(e) => {
								setModalMode("new")
								setIsNewConfigModalShown(true)
								e.stopPropagation()
							}}
							children={t("button.add")}
						/>
					}
					scrollRecords={{ set: true, bodyHeight: "calc(100vh - 310px)" }}
				/>

				{isNewConfigModalShown && (
					<NewConfigurationModal
						onClose={() => setIsNewConfigModalShown(false)}
						type="REQUEST"
						configData={
							modalMode == "new" ? defaultNewConfigValues : formStore.selectedFormsConfigId!
						}
						onSave={modalMode == "new" ? postSettingsNewConfig : updateConfig}
						modalTitle={modalMode == "new" ? t("rawText.new-config") : t("rawText.edit-config")}
						modalButtonName={modalMode == "new" ? t("button.add") : t("rawText.save")}
						newConfig={modalMode == "new"}
					/>
				)}
			</HyperDiv>
		</HyperDiv>
	)
})

export default FormConfigurationOverviewTable
